import { RBV_RP_COVERAGES, RBV_SECTION_COVERAGES, RBV_RP_CODE_TO_KEY } from './coverages';
import { RBV_PRODUCT, RBV_PP_DEFAULTS, RBV_RI_DEFAULTS, RBV_IN_EIGBVAK_DEFAULT } from './fields';

// Calculation payload builder + BFF-response merge for the SAA Rechtsbijstand screen.
// Extracted verbatim from RechtsbijstandScreen so the screen no longer carries ~215 lines of
// hardcoded product config + merge plumbing. Pure functions: the only impure inputs (today's
// date, the app URL) are injected, which also makes them unit-testable.

const n8 = (year: number, month: number, day: number) =>
  Number(`${year}${String(month).padStart(2, '0')}${String(day).padStart(2, '0')}`);

export interface BuildPayloadOptions {
  /** Final calc: only send selected coverages. Auto-calc (default): send all to price them. */
  selectedOnly?: boolean;
  /** Injected for testability; defaults to now. */
  now?: Date;
  /** AL_URLNAAM base; defaults to the current origin. */
  baseUrl?: string;
}

export function buildRechtsbijstandPayload(baseData: any, opts: BuildPayloadOptions = {}) {
  const selectedOnly = opts.selectedOnly ?? false;
  const today = opts.now ?? new Date();
  const baseUrl = opts.baseUrl ?? (typeof window !== 'undefined' ? window.location.origin + '/' : '/');

  const mappedGss = baseData?.Contractdocument?.PP?.VK?.VK_GEZSAMST || 'A';

  const dateN8 = n8(today.getFullYear(), today.getMonth() + 1, today.getDate());
  // Hoofdvervaldatum = first day of the current month, next year.
  const hvvDateN8 = n8(today.getFullYear() + 1, today.getMonth() + 1, 1);

  // BA: build the address array from stored address data (required by schema).
  const baData = baseData?.Contractdocument?.PP?.BA;
  const baItem: any = {};
  if (baData?.BA_PCODE) baItem.BA_PCODE = baData.BA_PCODE;
  if (baData?.BA_HUISNR) baItem.BA_HUISNR = Number(baData.BA_HUISNR);
  if (baData?.BA_TOEVOEG) baItem.BA_TOEVOEG = baData.BA_TOEVOEG;
  if (baData?.BA_STRAAT) baItem.BA_STRAAT = baData.BA_STRAAT;
  if (baData?.BA_PLAATS) baItem.BA_PLAATS = baData.BA_PLAATS;
  const baArray = Object.keys(baItem).length > 0 ? [baItem] : [];

  const dekkingen = baseData?.Supportsection?.Dekkingen || {};

  // WC (Consument & Wonen) — always included.
  const wc: any = {
    MP: [],
    WC_CODE: '9901',
    WC_MYCODE: '9330',
    WC_GSS: mappedGss,
    WC_ERB: 0,
    WC_EIGBVAK: 0,
    WC_VZBFRNS: 0,
    WC_PPRC: 20,
    WC_WPREMBP: '4',
  };

  // Section coverages (VK/IN/FV): included for auto-calc to get prices, or when selected.
  const includeVK = !selectedOnly || dekkingen.Verkeer === 'J';
  const vk: any | null = includeVK ? {
    VK_CODE: '9210',
    VK_ERB: 0,
    VK_VZBFRNS: 0,
    VK_EIGBVAK: 0,
    VK_PPRC: 20,
    VK_WPREMBP: '4',
  } : null;

  const includeIN = !selectedOnly || dekkingen.Inkomen === 'J';
  const inSection: any = includeIN ? {
    IN_CODE: '9903',
    IN_ERB: 0,
    IN_VZBFRNS: 0,
    IN_EIGBVAK: RBV_IN_EIGBVAK_DEFAULT,
    IN_PPRC: 20,
    IN_WPREMBP: '4',
  } : { IN_EIGBVAK: RBV_IN_EIGBVAK_DEFAULT };

  const includeFV = !selectedOnly || dekkingen.Fiscaal_Vermogen === 'J';
  const fv: any | null = includeFV ? {
    FV_CODE: '9905',
    FV_ERB: 0,
    FV_VZBFRNS: 0,
    FV_EIGBVAK: 0,
    FV_PPRC: 20,
    FV_WPREMBP: '4',
  } : null;

  // RP array — each optional dekking with its own RP_CODE (see coverages). Eigendom & Verhuur
  // (9520) additionally carries the rental-unit counts.
  const rpItems: any[] = [];
  const eigendomData = baseData?.Contractdocument?.PP?._rpEigendom || {};
  for (const cov of RBV_RP_COVERAGES) {
    const include = !selectedOnly || dekkingen[cov.key] === 'J';
    if (include) {
      const item: any = {
        RP_CODE: cov.code,
        RP_GSS: mappedGss,
        RP_PPRC: 20,
        RP_VZBFRNS: 0,
        RP_WPREMBP: '4',
      };
      if (cov.rentalUnits) {
        item.RP_VWE = eigendomData.RP_VWE ?? 1;
        item.RP_VBE = eigendomData.RP_VBE ?? 0;
        item.RP_VGAVRH = eigendomData.RP_VGAVRH ?? 0;
      }
      rpItems.push(item);
    }
  }

  const pp: any = {
    PP_BRANCHE: RBV_PRODUCT.branchCode,
    PP_MYAAND: baseData?.Contractdocument?.PP?.PP_MYAAND || RBV_PRODUCT.ppMyaand,
    PP_INGDAT: baseData?.Contractdocument?.PP?.PP_INGDAT || dateN8,
    PP_HVVDAT: baseData?.Contractdocument?.PP?.PP_HVVDAT || hvvDateN8,
    PP_BETTERM: baseData?.Contractdocument?.PP?.PP_BETTERM || 1,
    ...RBV_PP_DEFAULTS,
    PP_IBAN: baseData?.Contractdocument?.PP?.PP_IBAN || '',
    PP_PRODNM: RBV_PRODUCT.productName,
    PP_PRODUCT: RBV_PRODUCT.productName,
    PP_AFDDEFN: RBV_PRODUCT.afdDefn,
    PP_ADEFVRS: RBV_PRODUCT.adefVrs,
    PP_AFWAFST: '',
    PP_SPLIMNR: '',
    VP: {},
    BA: baArray,
    WC: wc,
    CL: [],
    VW: [{}],
    VE: { VE_MYAAND: RBV_PRODUCT.veMyaand },
    VL: {},
    TP: {},
    RI: { ...RBV_RI_DEFAULTS },
  };
  if (vk) pp.VK = vk;
  pp.IN = inSection;
  if (fv) pp.FV = fv;
  if (rpItems.length > 0) pp.RP = rpItems;

  const payload: any = {
    Contractdocument: {
      AL: {
        AL_APPLNM: RBV_PRODUCT.appName,
        AL_URLNAAM: baseUrl,
      },
      PP: pp,
    },
  };

  return {
    carrierCode: RBV_PRODUCT.carrierCode,
    productName: RBV_PRODUCT.productName,
    input: payload,
  };
}

// Merge a BFF calculate response into the working data, preserving user-entered fields the
// BFF doesn't echo back, and recompute PP_TTOT from the selected coverages.
export function mergeRechtsbijstandResult(bffOutput: any, prevData: any) {
  const mergedData = {
    ...prevData,
    ...JSON.parse(JSON.stringify(bffOutput)),
  };

  // Preserve user-entered address data (BA fields).
  if (prevData?.Contractdocument?.PP?.BA) {
    if (!mergedData.Contractdocument) mergedData.Contractdocument = {};
    if (!mergedData.Contractdocument.PP) mergedData.Contractdocument.PP = {};
    mergedData.Contractdocument.PP.BA = { ...prevData.Contractdocument.PP.BA };
  }

  // Preserve PP_BRANCHE (not returned by BFF but required for request endpoints).
  if (!mergedData.Contractdocument?.PP?.PP_BRANCHE && prevData?.Contractdocument?.PP?.PP_BRANCHE) {
    mergedData.Contractdocument.PP.PP_BRANCHE = prevData.Contractdocument.PP.PP_BRANCHE;
  }

  // Preserve _rpEigendom (rental unit counts — user entry, not in BFF response).
  if (prevData?.Contractdocument?.PP?._rpEigendom) {
    mergedData.Contractdocument.PP._rpEigendom = { ...prevData.Contractdocument.PP._rpEigendom };
  }

  // Preserve VK section (family composition — user entry).
  if (prevData?.Contractdocument?.PP?.VK) {
    mergedData.Contractdocument.PP.VK = {
      ...prevData.Contractdocument.PP.VK,
      ...mergedData.Contractdocument?.PP?.VK,
    };
  }

  // Preserve Supportsection selections.
  if (prevData?.Supportsection) {
    mergedData.Supportsection = { ...prevData.Supportsection };
  }

  // Strip XG (status info) — BFF returns it but the schema now places it at document level
  // and it must not be persisted back into downstream calculation requests.
  if (mergedData.Contractdocument) {
    delete mergedData.Contractdocument.XG;
  }

  // Compute PP_TTOT from WC (always) + only the selected optional coverages.
  const dekkingen = mergedData.Supportsection?.Dekkingen || {};
  const wcTtot = Number(mergedData.Contractdocument?.PP?.WC?.WC_TTOT || 0);
  const sectionTtot = RBV_SECTION_COVERAGES.reduce((sum: number, cov) => {
    const ttot = Number(mergedData.Contractdocument?.PP?.[cov.priceField!]?.[`${cov.priceField}_TTOT`] || 0);
    return sum + (dekkingen[cov.key] === 'J' ? ttot : 0);
  }, 0);
  const rpArray = Array.isArray(mergedData.Contractdocument?.PP?.RP) ? mergedData.Contractdocument.PP.RP : [];
  const rpTtot = rpArray.reduce((sum: number, rp: any) => {
    const dekkingKey = RBV_RP_CODE_TO_KEY[rp?.RP_CODE];
    return sum + (dekkingKey && dekkingen[dekkingKey] === 'J' ? Number(rp?.RP_TTOT || 0) : 0);
  }, 0);
  mergedData.Contractdocument.PP.PP_TTOT = wcTtot + sectionTtot + rpTtot;

  return mergedData;
}
